/**
 * Organisme → « Signature & tampon » (04/11/2026) : le dirigeant dépose l'image de sa signature et le tampon de
 * l'organisme, indique son nom et sa qualité, puis consent EXPLICITEMENT à leur apposition sur les documents produits au
 * nom de l'organisme. Retrait d'un clic. Historique tiré du journal d'audit. Réservé à l'administrateur de l'organisme.
 */
import { useEffect, useRef, useState, type DragEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Eye, ImageUp, ShieldCheck, ShieldOff, Trash2 } from "lucide-react";
import { api, ErreurApi, instantFr, type VueSignatureOrganisme } from "../api";
import { Alerte, Bouton, Carte, Champ, Chargement, cx, Etiquette, Modale, useNotifier } from "../ui/base";

const MAX_OCTETS = 500 * 1024;
const CLE = ["signature-organisme"];

type TypeImage = "signature" | "tampon";

function ZoneDepotImage({
  type,
  url,
  enCours,
  deposer,
  retirer,
}: {
  type: TypeImage;
  url: string | null;
  enCours: boolean;
  deposer: (f: File) => void;
  retirer: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const notifier = useNotifier();
  const [survol, setSurvol] = useState(false);
  const libelle = type === "signature" ? "Image de la signature" : "Tampon de l'organisme";
  const verifier = (f: File | undefined) => {
    if (!f) return;
    if (!["image/png", "image/jpeg"].includes(f.type))
      return notifier("danger", "Format refusé : déposez une image PNG ou JPG.");
    if (f.size > MAX_OCTETS)
      return notifier("danger", `Image trop lourde (${Math.round(f.size / 1024)} Ko) : 500 Ko au maximum.`);
    deposer(f);
  };
  const lacher = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setSurvol(false);
    verifier(e.dataTransfer.files[0]);
  };
  return (
    <div>
      <p className="text-sm font-medium">{libelle}</p>
      <div
        role="button"
        tabIndex={0}
        aria-label={`${libelle} : glisser-déposer ou choisir un fichier`}
        data-testid={`depot-${type}`}
        onClick={() => ref.current?.click()}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), ref.current?.click())}
        onDragOver={(e) => (e.preventDefault(), setSurvol(true))}
        onDragLeave={() => setSurvol(false)}
        onDrop={lacher}
        className={cx(
          "mt-2 grid h-40 cursor-pointer place-items-center rounded-md border-2 border-dashed p-3 text-center transition-colors",
          survol ? "border-accent bg-accent/5" : "border-trait-fort bg-papier hover:bg-papier-2",
        )}
        // Damier clair : un fond transparent se voit tel qu'il sera sur la page.
        style={
          url
            ? {
                backgroundImage:
                  "linear-gradient(45deg,#eef1ef 25%,transparent 25%,transparent 75%,#eef1ef 75%),linear-gradient(45deg,#eef1ef 25%,transparent 25%,transparent 75%,#eef1ef 75%)",
                backgroundSize: "16px 16px",
                backgroundPosition: "0 0,8px 8px",
              }
            : undefined
        }
      >
        {enCours ? (
          <Chargement libelle="Envoi…" />
        ) : url ? (
          <img src={url} alt={libelle} className="max-h-32 max-w-full object-contain" />
        ) : (
          <span className="flex flex-col items-center gap-1.5 text-[13px] text-encre-3">
            <ImageUp className="size-6" aria-hidden />
            Glissez l'image ici ou{" "}
            <span className="font-medium text-accent underline">choisissez un fichier</span>
            <span>PNG ou JPG, 500 Ko au plus</span>
          </span>
        )}
      </div>
      <input
        ref={ref}
        type="file"
        accept="image/png,image/jpeg"
        className="sr-only"
        tabIndex={-1}
        aria-label={`Fichier — ${libelle}`}
        onChange={(e) => {
          verifier(e.target.files?.[0]);
          e.target.value = "";
        }}
      />
      {url && (
        <div className="mt-2 flex gap-2">
          <Bouton
            taille="sm"
            onClick={() => ref.current?.click()}
            icone={<ImageUp className="size-4" aria-hidden />}
          >
            Remplacer
          </Bouton>
          <Bouton
            taille="sm"
            variante="discret"
            onClick={retirer}
            icone={<Trash2 className="size-4" aria-hidden />}
          >
            Retirer
          </Bouton>
        </div>
      )}
    </div>
  );
}

export function OngletSignatureOrganisme() {
  const requetes = useQueryClient();
  const notifier = useNotifier();
  const vue = useQuery({
    queryKey: CLE,
    queryFn: () => api.get<VueSignatureOrganisme>("/admin/signature-organisme"),
  });
  const [nom, setNom] = useState<string | null>(null);
  const [qualite, setQualite] = useState<string | null>(null);
  const [coche, setCoche] = useState(false);
  const [apercu, setApercu] = useState<number | null>(null);
  const maj = (r: VueSignatureOrganisme) => requetes.setQueryData(CLE, r);
  const erreur = (e: Error) => notifier("danger", e.message);

  const depot = useMutation({
    mutationFn: ({ type, f }: { type: TypeImage; f: File }) =>
      api.fichier<VueSignatureOrganisme>(`/admin/signature-organisme/${type}`, f),
    onSuccess: (r) => (maj(r), notifier("succes", "Image enregistrée.")),
    onError: erreur,
  });
  const retrait = useMutation({
    mutationFn: (type: TypeImage) => api.suppr<VueSignatureOrganisme>(`/admin/signature-organisme/${type}`),
    onSuccess: (r) => (maj(r), notifier("succes", "Image retirée.")),
    onError: erreur,
  });
  const signataire = useMutation({
    mutationFn: () =>
      api.put<VueSignatureOrganisme>("/admin/signature-organisme/signataire", {
        nom: nom ?? vue.data?.signataire_nom ?? "",
        qualite: qualite ?? vue.data?.signataire_qualite ?? "",
      }),
    onSuccess: (r) => (maj(r), setNom(null), setQualite(null), notifier("succes", "Signataire enregistré.")),
  });
  const consentir = useMutation({
    mutationFn: (version: string) =>
      api.post<VueSignatureOrganisme>("/admin/signature-organisme/consentement", { accepte: true, version }),
    onSuccess: (r) => (
      maj(r),
      setCoche(false),
      notifier("succes", "Consentement enregistré : votre signature sera apposée.")
    ),
    onError: erreur,
  });
  const retirerAccord = useMutation({
    mutationFn: () => api.suppr<VueSignatureOrganisme>("/admin/signature-organisme/consentement"),
    onSuccess: (r) => (maj(r), notifier("succes", "Consentement retiré : plus aucune apposition.")),
    onError: erreur,
  });

  const donnees = vue.data;
  useEffect(() => {
    // Première visite : le nom du représentant légal (Identité) est proposé, à confirmer.
    if (donnees && !donnees.signataire_nom && nom === null && donnees.signataire_suggere)
      setNom(donnees.signataire_suggere);
  }, [donnees, nom]);

  if (vue.isPending) return <Chargement />;
  if (vue.error || !donnees) return <Alerte ton="danger">{vue.error?.message ?? "Erreur"}</Alerte>;
  const v = donnees;
  const champsErr = signataire.error instanceof ErreurApi ? (signataire.error.details?.champs ?? {}) : {};
  const nomAffiche = nom ?? v.signataire_nom;
  const qualiteAffichee = qualite ?? v.signataire_qualite;
  const signataireModifie =
    (nom !== null && nom !== v.signataire_nom) || (qualite !== null && qualite !== v.signataire_qualite);
  const pretAConsentir = !!v.signature && !!v.signataire_nom && !!v.signataire_qualite && !signataireModifie;

  return (
    <div className="mt-6 space-y-6">
      <Carte className="p-5" data-testid="etat-consentement">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold">Apposition sur les documents</h2>
            <p className="mt-0.5 text-[13px] text-encre-3">
              Conventions, convocations, attestations, contrats de sous-traitance, mandats, engagements de
              confidentialité, factures de l'organisme.
            </p>
          </div>
          {v.consentement.actif ? (
            <Etiquette ton="accent">Active</Etiquette>
          ) : (
            <Etiquette ton="neutre">Inactive</Etiquette>
          )}
        </div>
        {v.consentement.actif ? (
          <p className="mt-3 text-sm">
            Consentement donné le <strong>{instantFr(v.consentement.le)}</strong>
            {v.consentement.par && (
              <>
                {" "}
                par <strong>{v.consentement.par}</strong>
              </>
            )}{" "}
            (texte {v.consentement.version}). Les documents produits à partir de maintenant portent la
            signature, le tampon, le nom, la qualité et la date.
          </p>
        ) : (
          <p className="mt-3 text-sm text-encre-2">
            Sans consentement actif, l'emplacement « Pour l'organisme » des documents reste vide : il se signe
            à la main.
          </p>
        )}
        <div className="mt-4 flex flex-wrap gap-2">
          <Bouton
            className="!h-auto min-h-10 !whitespace-normal py-2 text-left"
            icone={<Eye className="size-4 shrink-0" aria-hidden />}
            onClick={() => setApercu(Date.now())}
          >
            Prévisualiser une convention avec ma signature
          </Bouton>
          {v.consentement.actif && (
            <Bouton
              variante="danger"
              enCours={retirerAccord.isPending}
              icone={<ShieldOff className="size-4" aria-hidden />}
              onClick={() => retirerAccord.mutate()}
            >
              Retirer mon consentement
            </Bouton>
          )}
        </div>
      </Carte>

      {v.ancien_trace && (
        <Alerte ton="attention" titre="Ancien tracé de signature">
          Le tracé dessiné autrefois dans l'onglet Identité n'est plus apposé sur les documents. Déposez ici
          l'image de votre signature et donnez votre consentement pour la faire figurer à nouveau.
        </Alerte>
      )}

      <Carte className="p-5">
        <h2 className="text-base font-semibold">1. Images</h2>
        <p className="mt-0.5 text-[13px] text-encre-3">
          PNG ou JPG, 500 Ko au plus ; fond transparent conseillé (PNG). L'image est ré-enregistrée par la
          plateforme : métadonnées effacées, taille ajustée. Elle n'est jamais accessible par une adresse
          publique. Remplacer ou retirer une image suspend le consentement.
        </p>
        <div className="mt-4 grid gap-6 sm:grid-cols-2">
          <ZoneDepotImage
            type="signature"
            url={v.signature}
            enCours={depot.isPending && depot.variables?.type === "signature"}
            deposer={(f) => depot.mutate({ type: "signature", f })}
            retirer={() => retrait.mutate("signature")}
          />
          <ZoneDepotImage
            type="tampon"
            url={v.tampon}
            enCours={depot.isPending && depot.variables?.type === "tampon"}
            deposer={(f) => depot.mutate({ type: "tampon", f })}
            retirer={() => retrait.mutate("tampon")}
          />
        </div>
      </Carte>

      <Carte className="p-5">
        <h2 className="text-base font-semibold">2. Signataire</h2>
        <p className="mt-0.5 text-[13px] text-encre-3">
          Imprimés sous la signature, avec la date du document.
        </p>
        <form
          className="mt-4 grid gap-4 sm:grid-cols-[1fr_1fr_auto] sm:items-end"
          onSubmit={(e) => {
            e.preventDefault();
            signataire.mutate();
          }}
        >
          <Champ
            libelle="Nom du signataire"
            placeholder="Prénom NOM"
            value={nomAffiche}
            erreur={champsErr.signataire_nom}
            onChange={(e) => setNom(e.target.value)}
          />
          <Champ
            libelle="Qualité"
            placeholder="Président, Gérante…"
            value={qualiteAffichee}
            erreur={champsErr.signataire_qualite}
            onChange={(e) => setQualite(e.target.value)}
          />
          <Bouton
            type="submit"
            variante={signataireModifie ? "primaire" : "secondaire"}
            enCours={signataire.isPending}
          >
            Enregistrer
          </Bouton>
        </form>
        {signataire.error && !Object.keys(champsErr).length && (
          <p className="mt-2 text-[13px] text-danger">{signataire.error.message}</p>
        )}
      </Carte>

      <Carte className="p-5">
        <h2 className="text-base font-semibold">3. Consentement</h2>
        <p className="mt-0.5 text-[13px] text-encre-3">
          Texte version {v.texte.version}. Votre accord est horodaté par le serveur, avec votre nom et la
          version du texte.
        </p>
        {v.consentement.actif ? (
          <p className="mt-3 flex items-center gap-2 text-sm text-accent-fort">
            <ShieldCheck className="size-4" aria-hidden /> Consentement actif.
          </p>
        ) : (
          <form
            className="mt-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (coche) consentir.mutate(v.texte.version);
            }}
          >
            <label className="flex items-start gap-3 rounded-md border border-trait bg-papier p-3 text-sm">
              <input
                type="checkbox"
                className="mt-1 size-4 shrink-0 accent-[var(--color-accent)]"
                checked={coche}
                disabled={!pretAConsentir}
                onChange={(e) => setCoche(e.target.checked)}
              />
              <span>{v.texte.contenu}</span>
            </label>
            {!pretAConsentir && (
              <p className="mt-2 text-[13px] text-encre-3">
                Déposez d'abord l'image de votre signature et enregistrez votre nom et votre qualité.
              </p>
            )}
            <Bouton
              type="submit"
              variante="primaire"
              className="mt-3"
              disabled={!coche || !pretAConsentir}
              enCours={consentir.isPending}
              icone={<ShieldCheck className="size-4" aria-hidden />}
            >
              Donner mon consentement
            </Bouton>
          </form>
        )}
      </Carte>

      <Carte className="p-5">
        <h2 className="text-base font-semibold">Historique</h2>
        {v.historique.length === 0 ? (
          <p className="mt-2 text-sm text-encre-3">Aucun dépôt ni consentement pour l'instant.</p>
        ) : (
          <ul className="mt-3 divide-y divide-trait text-sm" data-testid="historique-signature">
            {v.historique.map((h, i) => (
              <li key={i} className="flex flex-wrap justify-between gap-x-4 gap-y-0.5 py-2">
                <span>{h.libelle}</span>
                <span className="text-[13px] text-encre-3">
                  {instantFr(h.le)}
                  {h.par && ` — ${h.par}`}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Carte>

      <Modale
        ouverte={apercu !== null}
        fermer={() => setApercu(null)}
        titre="Aperçu d'une convention (dossier fictif)"
        large
      >
        {apercu !== null && (
          <iframe
            title="Aperçu d'une convention"
            src={`/api/admin/signature-organisme/apercu-convention?v=${apercu}`}
            sandbox=""
            className="h-[70dvh] w-full rounded-md border border-trait bg-papier-3"
          />
        )}
      </Modale>
    </div>
  );
}
